import { z } from 'zod';
import { CommercialMode } from './enums';

// Cidades de interesse do CRM (issue #252) — a campanha de conquista geográfica.
//
// Diferença para `crm_goals`: lá a meta é do COLABORADOR (alvo semanal por
// vendedor, ver `CrmGoalSchema`); aqui a meta é da PRAÇA e tem prazo de campanha
// ("São Paulo: 245 até 31/12"). São eixos independentes — um vendedor pode bater
// a meta semanal dele vendendo tudo numa cidade só.
//
// O "realizado" de uma cidade é contado em TENANTS PAGANTES do app principal
// (`billing/subscription.external_subscription_id != null`), não em leads WON do
// CRM: lead ganho vira cliente só quando o pagamento entra, e é o pagante que a
// campanha persegue. Leads abertos aparecem num campo à parte, para responder
// "estamos ao menos prospectando onde prometemos crescer?".

// ---------------------------------------------------------------------------
// Chave de cidade
// ---------------------------------------------------------------------------

/**
 * Remove acentos e baixa a caixa. Mesmo algoritmo do `deburr` do cliente CRM
 * (`apps/crm/src/lib/countries.ts`), replicado aqui porque a chave precisa sair
 * IDÊNTICA no cliente e no servidor — e `packages/types` não pode importar de
 * `apps/`.
 */
function deburrLower(value: string): string {
  return value.normalize('NFD').replace(/\p{Diacritic}/gu, '').toLowerCase();
}

/**
 * Chave canônica de uma cidade: `${país}-${UF}-${slug}` (ex.: `BR-SP-sao-paulo`).
 *
 * É o docId de `crm_cities` e de `crm_city_stats`, e o elo entre um tenant (cuja
 * cidade é DERIVADA do endereço) e a cidade da lista. Por isso a normalização é
 * agressiva de propósito: "São Paulo", "sao paulo" e "SÃO  PAULO" têm que cair na
 * mesma chave, senão a mesma praça vira duas linhas na tela.
 *
 * NÃO desambigua homônimos entre UFs diferentes — a UF está na chave justamente
 * por isso (`BR-MG-belo-horizonte` ≠ `BR-PA-belo-horizonte`).
 */
export function crmCityKey(country: string, state: string, city: string): string {
  const slug = deburrLower(city.trim()).replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
  return `${country.trim().toUpperCase()}-${state.trim().toUpperCase()}-${slug}`;
}

// ---------------------------------------------------------------------------
// Geografia BR
// ---------------------------------------------------------------------------

export const CrmBrRegionSchema = z.enum([
  'NORTE',
  'NORDESTE',
  'CENTRO_OESTE',
  'SUDESTE',
  'SUL',
]);
export type CrmBrRegion = z.infer<typeof CrmBrRegionSchema>;

/**
 * UF → região do IBGE. Usado para os totais por região na faixa de KPIs
 * ("Sudeste 1.610 · Sul 780"). Fora do BR o mapeamento não se aplica e a cidade
 * fica sem região.
 */
export const CRM_BR_REGION_BY_UF: Readonly<Record<string, CrmBrRegion>> = Object.freeze({
  AC: 'NORTE', AP: 'NORTE', AM: 'NORTE', PA: 'NORTE', RO: 'NORTE', RR: 'NORTE', TO: 'NORTE',
  AL: 'NORDESTE', BA: 'NORDESTE', CE: 'NORDESTE', MA: 'NORDESTE', PB: 'NORDESTE',
  PE: 'NORDESTE', PI: 'NORDESTE', RN: 'NORDESTE', SE: 'NORDESTE',
  DF: 'CENTRO_OESTE', GO: 'CENTRO_OESTE', MT: 'CENTRO_OESTE', MS: 'CENTRO_OESTE',
  ES: 'SUDESTE', MG: 'SUDESTE', RJ: 'SUDESTE', SP: 'SUDESTE',
  PR: 'SUL', RS: 'SUL', SC: 'SUL',
});

/** Região da UF, ou `null` quando a sigla não é brasileira. */
export function crmBrRegionOf(state: string): CrmBrRegion | null {
  return CRM_BR_REGION_BY_UF[state.trim().toUpperCase()] ?? null;
}

// ---------------------------------------------------------------------------
// `crm_cities/{cityKey}`
// ---------------------------------------------------------------------------

/**
 * Nível de ataque da praça — decide para onde vai SDR, tráfego e visita, e é o
 * que dá tamanho/cor à bolha no mapa. Não é derivado da meta: uma cidade pequena
 * pode ser A+ por razão estratégica (hub de rede, referência de mercado).
 *
 * `A_PLUS` em vez de `A+` porque o valor entra em chave de i18n
 * (`cities.priority.A_PLUS`) e em atributo de dado.
 */
export const CrmCityPrioritySchema = z.enum(['A_PLUS', 'A', 'B', 'C']);
export type CrmCityPriority = z.infer<typeof CrmCityPrioritySchema>;

/** Ordem de ataque, do mais agressivo ao oportunístico (ordena a tabela). */
export const CRM_CITY_PRIORITY_ORDER: readonly CrmCityPriority[] = ['A_PLUS', 'A', 'B', 'C'];

/** Peso numérico da prioridade — para ordenação estável na tabela e no mapa. */
export function crmCityPriorityRank(priority: CrmCityPriority): number {
  const index = CRM_CITY_PRIORITY_ORDER.indexOf(priority);
  return index === -1 ? CRM_CITY_PRIORITY_ORDER.length : index;
}

/**
 * Cidade de interesse. Documento em `crm_cities/{cityKey}` — escrito só por
 * callable ADMIN (rules em default-deny).
 *
 * `latitude`/`longitude` são NULLABLE porque a geocodificação pode falhar: a
 * cidade continua valendo na tabela (a meta é o dado que importa) e some só do
 * mapa, com aviso e ação de correção. Deixar a cidade de fora por falta de
 * coordenada seria perder a meta por um detalhe de apresentação.
 */
export const CrmCitySchema = z.object({
  cityKey: z.string().min(1),
  name: z.string().min(1).max(120),
  /** UF em 2 letras maiúsculas (BR). Fora do BR, o código de subdivisão local. */
  state: z.string().min(2).max(3),
  countryCode: z.string().length(2),
  priority: CrmCityPrioritySchema,
  /** Meta de clientes PAGANTES na praça até o prazo da campanha. */
  target: z.number().int().nonnegative(),
  latitude: z.number().min(-90).max(90).nullable().optional(),
  longitude: z.number().min(-180).max(180).nullable().optional(),
  placeId: z.string().max(300).nullable().optional(),
  active: z.boolean().default(true),
  createdBy: z.string().nullable().optional(),
  createdAt: z.number().int().nonnegative().nullable().optional(),
  updatedAt: z.number().int().nonnegative().nullable().optional(),
});
export type CrmCity = z.infer<typeof CrmCitySchema>;

// ---------------------------------------------------------------------------
// `crm_tenant_geo/{tenantId}`
// ---------------------------------------------------------------------------

/**
 * Como a cidade do tenant foi descoberta. Fica no doc (e numa coluna da tela)
 * porque a confiança é diferente em cada caminho: `ADDRESS_PARSE` lê o
 * `formatted_address` que o próprio Google devolveu no onboarding;
 * `REVERSE_GEOCODE` parte só da coordenada e pode cair no município vizinho numa
 * divisa. Sem esse campo, um número errado na tela não teria como ser explicado.
 */
export const CrmTenantGeoSourceSchema = z.enum([
  'ADDRESS_PARSE',
  'REVERSE_GEOCODE',
  'UNRESOLVED',
]);
export type CrmTenantGeoSource = z.infer<typeof CrmTenantGeoSourceSchema>;

/**
 * Cache da geolocalização de um tenant do app principal, em
 * `crm_tenant_geo/{tenantId}` — escrito SÓ pelo job `crmAggregateCityStats`.
 *
 * Existe porque `/tenants` guarda `latitude`/`longitude`/`address`/`placeId` mas
 * NÃO guarda `city`/`state` estruturados: agrupar tenant por cidade exige derivar
 * isso, e derivar a cada leitura significaria reverse-geocode em loop. O cache é
 * revalidado só quando o endereço ou as coordenadas do tenant mudam
 * (`addressSignature`).
 */
export const CrmTenantGeoSchema = z.object({
  tenantId: z.string().min(1),
  /** Nome do estabelecimento — é o que aparece no popup do clique no mapa. */
  name: z.string(),
  latitude: z.number().min(-90).max(90).nullable(),
  longitude: z.number().min(-180).max(180).nullable(),
  city: z.string().nullable(),
  state: z.string().nullable(),
  countryCode: z.string().nullable(),
  /** `null` quando a cidade não pôde ser resolvida (fica fora de toda contagem). */
  cityKey: z.string().nullable(),
  source: CrmTenantGeoSourceSchema,
  /** Status do tenant no app principal (`ACTIVE`, `SUSPENDED_BILLING`, …). */
  tenantStatus: z.string(),
  /** `true` só com assinatura de verdade — cortesia e demo ficam de fora. */
  paying: z.boolean(),
  /**
   * #743 (9.11) — de onde vem o cliente: venda nossa (`DIRECT`), do parceiro
   * (`PARTNER`, rótulo "via parceiro") ou cortesia. `null` = ainda não é
   * cliente (cadastro sem pagamento, suspenso). A META da cidade conta só
   * `paying` (DIRECT do time interno); o mapa mostra os três.
   * `.catch(null)`: doc gravado antes do campo continua legível.
   */
  commercialOrigin: z.enum([CommercialMode.DIRECT, CommercialMode.PARTNER, CommercialMode.COURTESY]).nullable().optional().catch(null),
  /** Hash do endereço+coords que gerou esta resolução; muda ⇒ resolve de novo. */
  addressSignature: z.string(),
  resolvedAt: z.number().int().nonnegative(),
});
export type CrmTenantGeo = z.infer<typeof CrmTenantGeoSchema>;

// ---------------------------------------------------------------------------
// `crm_city_stats/{cityKey}`
// ---------------------------------------------------------------------------

/**
 * Agregado por cidade, em `crm_city_stats/{cityKey}` — escrito só pelo job.
 *
 * Existe uma linha para toda cidade que tem ao menos um tenant OU está na lista
 * de interesse. Ou seja: cidade fora da lista com cliente aparece no mapa
 * (ordem do owner — venda não some do radar por a praça não estar na lista), e
 * cidade da lista sem nenhum cliente aparece zerada (é justamente o gap).
 *
 * As coordenadas aqui são o CENTROIDE dos tenants quando a cidade não está na
 * lista (não temos geocodificação dela); para cidade da lista, o job copia as
 * coordenadas cadastradas.
 */
export const CrmCityStatsSchema = z.object({
  cityKey: z.string().min(1),
  name: z.string(),
  state: z.string(),
  countryCode: z.string(),
  latitude: z.number().min(-90).max(90).nullable(),
  longitude: z.number().min(-180).max(180).nullable(),
  /** Tenants com assinatura paga — é o numerador do "18/245". */
  payingCount: z.number().int().nonnegative(),
  /** Cortesia, suspenso e afins. Contexto na tabela; fora do "18/245". */
  otherTenantCount: z.number().int().nonnegative(),
  /** Leads `OPEN` do CRM naquela cidade — o termômetro de prospecção. */
  openLeadCount: z.number().int().nonnegative(),
  updatedAt: z.number().int().nonnegative(),
});
export type CrmCityStats = z.infer<typeof CrmCityStatsSchema>;

// ---------------------------------------------------------------------------
// `crm_appConfig/cityGoals`
// ---------------------------------------------------------------------------

/**
 * Config da campanha. `deadline` é uma data de PAREDE (`YYYY-MM-DD`, sem hora e
 * sem fuso): "até 31/12" é uma promessa de calendário, não um instante — gravar
 * millis obrigaria a escolher um fuso e faria a data virar 30/12 para metade do
 * time.
 *
 * `globalTarget` é a meta da empresa inteira (3.000), maior que a soma das
 * cidades: a diferença é inbound nacional, parcerias e praças fora da lista. A
 * tela mostra os dois para que ninguém confunda "meta das cidades" com "meta".
 */
export const CrmCityGoalsConfigSchema = z.object({
  deadline: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).nullable(),
  globalTarget: z.number().int().nonnegative().nullable(),
  updatedBy: z.string().nullable().optional(),
  updatedAt: z.number().int().nonnegative().nullable().optional(),
});
export type CrmCityGoalsConfig = z.infer<typeof CrmCityGoalsConfigSchema>;

// ---------------------------------------------------------------------------
// Contrato dos callables
// ---------------------------------------------------------------------------

/** Cidade da lista já cruzada com o agregado — uma linha da tabela. */
export const CrmCityRowSchema = z.object({
  cityKey: z.string(),
  name: z.string(),
  state: z.string(),
  countryCode: z.string(),
  region: CrmBrRegionSchema.nullable(),
  priority: CrmCityPrioritySchema,
  target: z.number().int().nonnegative(),
  latitude: z.number().nullable(),
  longitude: z.number().nullable(),
  payingCount: z.number().int().nonnegative(),
  otherTenantCount: z.number().int().nonnegative(),
  openLeadCount: z.number().int().nonnegative(),
  /** `max(0, target − payingCount)` — calculado no servidor para não divergir. */
  missing: z.number().int().nonnegative(),
});
export type CrmCityRow = z.infer<typeof CrmCityRowSchema>;

/**
 * Cidade que TEM cliente mas não está na lista de interesse. Vai separada de
 * `CrmCityRow` porque não tem meta nem prioridade — desenhar as duas com o mesmo
 * tipo convidaria a inventar `target: 0` e a somar essas cidades no total da
 * campanha, que é exatamente o erro que se quer evitar.
 */
export const CrmOffListCitySchema = z.object({
  cityKey: z.string(),
  name: z.string(),
  state: z.string(),
  countryCode: z.string(),
  latitude: z.number().nullable(),
  longitude: z.number().nullable(),
  payingCount: z.number().int().nonnegative(),
  otherTenantCount: z.number().int().nonnegative(),
  openLeadCount: z.number().int().nonnegative(),
});
export type CrmOffListCity = z.infer<typeof CrmOffListCitySchema>;

/** Tenant que ficou sem cidade resolvida, mas tem coordenada — vira ponto solto. */
export const CrmUnplacedTenantSchema = z.object({
  tenantId: z.string(),
  name: z.string(),
  latitude: z.number(),
  longitude: z.number(),
  paying: z.boolean(),
});
export type CrmUnplacedTenant = z.infer<typeof CrmUnplacedTenantSchema>;

/** Totais da campanha. `citiesTarget` é a soma das metas cadastradas. */
export const CrmCityTotalsSchema = z.object({
  cityCount: z.number().int().nonnegative(),
  citiesTarget: z.number().int().nonnegative(),
  payingInListedCities: z.number().int().nonnegative(),
  payingOffList: z.number().int().nonnegative(),
  /** Tenants pagantes sem cidade resolvida — o tamanho do ponto cego. */
  payingUnresolved: z.number().int().nonnegative(),
  openLeadsInListedCities: z.number().int().nonnegative(),
  missing: z.number().int().nonnegative(),
});
export type CrmCityTotals = z.infer<typeof CrmCityTotalsSchema>;

/** Total por UF — alimenta o "Resumo da guerra" por estado/região. */
export const CrmCityStateTotalSchema = z.object({
  state: z.string(),
  region: CrmBrRegionSchema.nullable(),
  target: z.number().int().nonnegative(),
  paying: z.number().int().nonnegative(),
  openLeads: z.number().int().nonnegative(),
});
export type CrmCityStateTotal = z.infer<typeof CrmCityStateTotalSchema>;

export const CrmListCitiesInputSchema = z.object({});
export type CrmListCitiesInput = z.infer<typeof CrmListCitiesInputSchema>;

export const CrmListCitiesResultSchema = z.object({
  cities: z.array(CrmCityRowSchema),
  totals: CrmCityTotalsSchema,
  byState: z.array(CrmCityStateTotalSchema),
  config: CrmCityGoalsConfigSchema,
  /** Quando o job agregou pela última vez (`null` = nunca rodou). */
  statsUpdatedAt: z.number().int().nonnegative().nullable(),
});
export type CrmListCitiesResult = z.infer<typeof CrmListCitiesResultSchema>;

/**
 * Upsert de cidade. `cityKey` é DERIVADO de país+UF+nome no servidor (nunca vem
 * do cliente): deixar o cliente escolher o docId abriria a porta para duas
 * linhas da mesma praça com chaves diferentes, e o cruzamento com os tenants —
 * que usa a mesma derivação — deixaria de bater.
 */
export const CrmUpsertCityInputSchema = z.object({
  name: z.string().min(2).max(120),
  state: z.string().min(2).max(3),
  countryCode: z.string().length(2).default('BR'),
  priority: CrmCityPrioritySchema,
  target: z.number().int().nonnegative().max(1_000_000),
  latitude: z.number().min(-90).max(90).nullable().optional(),
  longitude: z.number().min(-180).max(180).nullable().optional(),
  placeId: z.string().max(300).nullable().optional(),
});
export type CrmUpsertCityInput = z.infer<typeof CrmUpsertCityInputSchema>;

export const CrmDeleteCityInputSchema = z.object({ cityKey: z.string().min(1) });
export type CrmDeleteCityInput = z.infer<typeof CrmDeleteCityInputSchema>;

/**
 * Importação do lote inicial. Idempotente por decisão de produto: cidade que já
 * existe é PULADA, nunca sobrescrita — quem já ajustou a meta de São Paulo à mão
 * não pode perder o ajuste porque alguém clicou no botão de novo.
 */
export const CrmSeedCitiesInputSchema = z.object({
  cities: z.array(CrmUpsertCityInputSchema).min(1).max(500),
});
export type CrmSeedCitiesInput = z.infer<typeof CrmSeedCitiesInputSchema>;

export const CrmSeedCitiesResultSchema = z.object({
  created: z.number().int().nonnegative(),
  skipped: z.number().int().nonnegative(),
});
export type CrmSeedCitiesResult = z.infer<typeof CrmSeedCitiesResultSchema>;

export const CrmUpdateCityGoalsConfigInputSchema = z.object({
  deadline: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).nullable(),
  globalTarget: z.number().int().nonnegative().max(10_000_000).nullable(),
});
export type CrmUpdateCityGoalsConfigInput = z.infer<typeof CrmUpdateCityGoalsConfigInputSchema>;

export const CrmCityCoverageInputSchema = z.object({});
export type CrmCityCoverageInput = z.infer<typeof CrmCityCoverageInputSchema>;

/** Payload do mapa: as três camadas de desenho, já separadas por natureza. */
export const CrmCityCoverageResultSchema = z.object({
  cities: z.array(CrmCityRowSchema),
  offList: z.array(CrmOffListCitySchema),
  unplaced: z.array(CrmUnplacedTenantSchema),
  totals: CrmCityTotalsSchema,
  statsUpdatedAt: z.number().int().nonnegative().nullable(),
});
export type CrmCityCoverageResult = z.infer<typeof CrmCityCoverageResultSchema>;

export const CrmCityTenantsInputSchema = z.object({ cityKey: z.string().min(1) });
export type CrmCityTenantsInput = z.infer<typeof CrmCityTenantsInputSchema>;

/** Um estabelecimento na lista do popup. */
export const CrmCityTenantEntrySchema = z.object({
  tenantId: z.string(),
  name: z.string(),
  paying: z.boolean(),
  /** #743 (9.11) — `PARTNER` vira o rótulo "via parceiro" na lista da cidade. */
  commercialOrigin: z.enum([CommercialMode.DIRECT, CommercialMode.PARTNER, CommercialMode.COURTESY]).nullable().optional(),
  tenantStatus: z.string(),
  source: CrmTenantGeoSourceSchema,
});
export type CrmCityTenantEntry = z.infer<typeof CrmCityTenantEntrySchema>;

/** Teto de itens no popup do mapa — acima disso a lista deixa de ser útil. */
export const CRM_CITY_TENANTS_LIMIT = 500;

export const CrmCityTenantsResultSchema = z.object({
  cityKey: z.string(),
  tenants: z.array(CrmCityTenantEntrySchema),
  /** `true` quando o teto cortou a lista (a UI avisa em vez de mentir). */
  truncated: z.boolean(),
});
export type CrmCityTenantsResult = z.infer<typeof CrmCityTenantsResultSchema>;

export const CrmRefreshCityStatsInputSchema = z.object({});
export type CrmRefreshCityStatsInput = z.infer<typeof CrmRefreshCityStatsInputSchema>;

export const CrmRefreshCityStatsResultSchema = z.object({
  tenantsScanned: z.number().int().nonnegative(),
  tenantsResolved: z.number().int().nonnegative(),
  tenantsUnresolved: z.number().int().nonnegative(),
  citiesWritten: z.number().int().nonnegative(),
  leadsScanned: z.number().int().nonnegative(),
  updatedAt: z.number().int().nonnegative(),
});
export type CrmRefreshCityStatsResult = z.infer<typeof CrmRefreshCityStatsResultSchema>;
